import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Alert, Button, Card, Container, ProgressBar } from "react-bootstrap";
import {
  ArrowLeft,
  CheckCircle,
  FileEarmarkBarGraph,
  Lock,
  Plus,
  Unlock,
  XCircle,
} from "react-bootstrap-icons";
import { WithApiAccess } from "../../../lib/HigherOrderComponents/WithApiAccess";
import { withHeaderMessages } from "../../../higherOrderComponents/WithHeaderMessages";
import { LoadingSpinner } from "../../../lib/components/LoadingSpinner";
import { FormattedDate } from "../../../lib/components/FormattedDate";
import ToastMessage from "../../ToastMessage";
import { usePayoutRun } from "./hooks/usePayoutRun";
import { PageHeader } from "./components/PageHeader";
import { SummaryTotals } from "./components/SummaryTotals";
import { AgentPanel } from "./components/AgentPanel";
import { AdjustModal } from "./components/AdjustModal";
import { AgentAdjustmentModal } from "./components/AgentAdjustmentModal";
import { RejectModal } from "./components/RejectModal";
import { RunDecisionModal } from "./components/RunDecisionModal";
import { ConfirmActionModal } from "./components/ConfirmActionModal";
import { RequestReportModal } from "./components/RequestReportModal";
import { PayoutRunReportsModal } from "./components/PayoutRunReportsModal";
import { AddContractModal } from "./components/AddContractModal";
import { PayoutRunActionBar } from "./components/PayoutRunActionBar";
import { HeaderButton } from "../shared";
import { groupByAgent } from "./utils/groupByAgent";
import {
  buildSelectionRows,
  isAgentGroupFullyReviewed,
} from "./utils/recordSelection";
import {
  createInitialToastState,
  createToastState,
  hideToastState,
} from "./utils/toastHelpers";
import { payoutRunToasts } from "./utils/payoutRunToasts";
import "../Commissions.css";

const LOCKED_STATUSES = new Set(["approved", "declined", "locked"]);

function formatReportMetaDetails(report) {
  if (!report) return [];
  const details = [];

  const archiveStr = report.archiveType
    ? String(report.archiveType).toLowerCase()
    : null;
  const fileCountStr = report.fileCount ? `${report.fileCount} files` : null;
  const fileTypesStr =
    Array.isArray(report.fileTypes) && report.fileTypes.length
      ? report.fileTypes.join(", ")
      : typeof report.fileTypes === "string"
        ? report.fileTypes
        : null;

  let filesSpec = "";
  if (archiveStr && fileCountStr) {
    filesSpec = `${archiveStr}, ${fileCountStr}`;
  } else if (archiveStr) {
    filesSpec = archiveStr;
  } else if (fileCountStr) {
    filesSpec = fileCountStr;
  }

  if (filesSpec && fileTypesStr) {
    details.push(`${filesSpec} · ${fileTypesStr}`);
  } else if (filesSpec) {
    details.push(filesSpec);
  } else if (fileTypesStr) {
    details.push(fileTypesStr);
  }

  if (report.fileSize) {
    details.push(report.fileSize);
  }

  if (report.statusNote) {
    details.push(report.statusNote);
  }

  if (report.createdAt) {
    details.push(
      <FormattedDate
        key="created-at"
        date={report.createdAt}
        format="MM/DD h:mm A"
      />,
    );
  }

  return details;
}

function normalizeStatus(status) {
  if (status == null) return status;
  return String(status).trim().toLowerCase();
}

function countByStatus(run) {
  const buckets = {
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  };
  if (!run) return buckets;

  const tally = (status) => {
    const s = normalizeStatus(status);

    // Count every visible commission/bonus row toward review workload.
    buckets.total += 1;

    switch (s) {
      case "approved":
      case "paid":
        buckets.approved += 1;
        return;
      case "declined":
      case "rejected":
        buckets.rejected += 1;
        return;
      default:
        buckets.pending += 1;
        return;
    }
  };

  (run.agentContracts ?? []).forEach((ac) => tally(ac?.status));
  (run.bonusRecords ?? []).forEach((bonus) => tally(bonus?.status));
  (run.chargebackRecords ?? []).forEach((chargeback) =>
    tally(chargeback?.status),
  );
  return buckets;
}

function formatCurrency(n) {
  return Number(n ?? 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

function getAdjustmentId(adjustment) {
  return (
    adjustment?.payoutRunAgentAdjustmentId ??
    adjustment?.agentAdjustmentId ??
    adjustment?.id ??
    null
  );
}

function getRecordActionKey(target) {
  if (!target) return null;
  if (target.kind === "commission") {
    return `commission:${target.agentId || ""}:${target.id || target.contractNumber || ""}`;
  }
  if (target.kind === "bonus") {
    return `bonus:${target.id || ""}`;
  }
  if (target.kind === "chargeback") {
    return `chargeback:${target.id || ""}`;
  }
  return null;
}

function parseFilenameFromDisposition(disposition) {
  if (!disposition || disposition.indexOf("attachment") === -1) return "";
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(disposition);
  return matches?.[1]
    ? matches[1].replace(/["']/g, "").trim().replace(/^_+|_+$/g, "")
    : "";
}

function triggerDownload(responseData, filename) {
  const blob = new Blob([responseData], { type: responseData?.type });
  const blobUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = blobUrl;
  anchor.download = filename || "commission-report";
  document.body.appendChild(anchor);
  anchor.click();

  setTimeout(() => {
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(blobUrl);
  }, 100);
}

function addRecordKey(setKeys, key) {
  setKeys((prev) => {
    const next = new Set(prev);
    next.add(key);
    return next;
  });
}

function removeRecordKey(setKeys, key) {
  setKeys((prev) => {
    const next = new Set(prev);
    next.delete(key);
    return next;
  });
}

const PayoutRun = (props) => {
  const { payoutRunId } = useParams();
  const [toast, setToast] = useState(createInitialToastState);

  const showToast = (message, type = "success", action = "info") => {
    setToast(createToastState(message, type, action));
  };

  const showToastPayload = ({ message, type, action }) => {
    showToast(message, type, action);
  };

  const hideToast = () => {
    setToast(hideToastState);
  };

  const {
    run,
    isLoading,
    reportTypes,
    isLoadingReportTypes,
    fetchRun,
    saveAgentAdjustment,
    deleteAgentAdjustment,
    updateRecordStatus,
    sendRecordBackToPool,
    addRecordAdjustment,
    submitRunDecision,
    addAgentContractToPayoutRun,
    addChargebackToPayoutRun,
    requestCommissionReport,
    downloadCommissionReport,
    fetchCommissionReportTypes,
  } = usePayoutRun({
    api: props.api,
    payoutRunId,
    onError: () => showToastPayload(payoutRunToasts.loadRunError()),
  });

  const [collapsed, setCollapsed] = useState(() => new Set());
  const autoCollapsedAgentKeys = useRef(new Set());
  const [expandedCommissionRows, setExpandedCommissionRows] = useState(
    () => new Set(),
  );
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [agentAdjustmentTarget, setAgentAdjustmentTarget] = useState(null);
  const [isSavingAgentAdjustment, setIsSavingAgentAdjustment] = useState(false);
  const [isDeletingAdjustment, setIsDeletingAdjustment] = useState(false);
  const [deleteAdjustmentTarget, setDeleteAdjustmentTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [isSubmittingRecordAction, setIsSubmittingRecordAction] =
    useState(false);
  const [approvingRecordKeys, setApprovingRecordKeys] = useState(
    () => new Set(),
  );
  const [removingRecordKeys, setRemovingRecordKeys] = useState(() => new Set());
  const [runDecision, setRunDecision] = useState(null); // 'approve' | 'decline' | null
  const [isSubmittingRunDecision, setIsSubmittingRunDecision] = useState(false);
  const [isRequestReportModalOpen, setIsRequestReportModalOpen] =
    useState(false);
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  const [isAddContractModalOpen, setIsAddContractModalOpen] = useState(false);
  const [isSubmittingReportRequest, setIsSubmittingReportRequest] =
    useState(false);
  const [isSubmittingAddContract, setIsSubmittingAddContract] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [bulkRejectTarget, setBulkRejectTarget] = useState(null);
  const [approvingAgentKey, setApprovingAgentKey] = useState(null);

  const toggle = (setter) => (key) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleCollapse = toggle(setCollapsed);
  const toggleCommissionRow = toggle(setExpandedCommissionRows);

  const groupedByAgent = useMemo(() => groupByAgent(run), [run]);
  const reportAgentOptions = useMemo(
    () =>
      groupedByAgent.map((agentGroup) => ({
        key: agentGroup.key,
        label: agentGroup.agentLabel,
      })),
    [groupedByAgent],
  );
  const reportTypeOptions = useMemo(
    () =>
      (reportTypes ?? []).map((reportType) => ({
        key: String(reportType.reportId),
        value: String(reportType.reportId),
        label: reportType.friendlyName,
      })),
    [reportTypes],
  );
  const isLocked = run ? LOCKED_STATUSES.has(run.status) : false;
  const isDeclined = run ? run.status === "declined" : false;
  const counts = useMemo(() => countByStatus(run), [run]);
  const showNotFoundAlert = !isLoading && !run && !toast.show;

  const selectionRows = useMemo(
    () => buildSelectionRows(groupedByAgent),
    [groupedByAgent],
  );

  // Collapse an agent the first time it becomes fully approved, but let the user reopen it.
  useEffect(() => {
    const autoCollapsed = autoCollapsedAgentKeys.current;
    const newlyApproved = [];

    groupedByAgent.forEach((agentGroup) => {
      if (isAgentGroupFullyReviewed(agentGroup)) {
        if (!autoCollapsed.has(agentGroup.key)) {
          newlyApproved.push(agentGroup.key);
        }
      } else {
        autoCollapsed.delete(agentGroup.key);
      }
    });

    if (newlyApproved.length === 0) return;

    newlyApproved.forEach((key) => autoCollapsed.add(key));
    setCollapsed((prev) => {
      const next = new Set(prev);
      newlyApproved.forEach((key) => next.add(key));
      return next;
    });
  }, [groupedByAgent]);

  // Drop keys for rows that disappeared or were finalized by the last refresh.
  useEffect(() => {
    const selectableKeys = new Set(
      selectionRows.filter((r) => r.selectable).map((r) => r.key),
    );
    setSelectedKeys((prev) => {
      const next = new Set();
      prev.forEach((key) => {
        if (selectableKeys.has(key)) next.add(key);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [selectionRows]);

  const selectedRows = useMemo(
    () => selectionRows.filter((r) => selectedKeys.has(r.key)),
    [selectionRows, selectedKeys],
  );

  const selectionSummary = useMemo(
    () =>
      selectedRows.reduce(
        (acc, row) => ({
          amount: acc.amount + row.amount,
          contracts: acc.contracts + (row.kind === "commission" ? 1 : 0),
          bonuses: acc.bonuses + (row.kind === "bonus" ? 1 : 0),
          chargebacks: acc.chargebacks + (row.kind === "chargeback" ? 1 : 0),
        }),
        { amount: 0, contracts: 0, bonuses: 0, chargebacks: 0 },
      ),
    [selectedRows],
  );

  const clearSelection = () => {
    setSelectedKeys(new Set());
    setBulkRejectTarget(null);
  };

  const toggleSelectRow = (row) => {
    if (!row?.selectable) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(row.key) ? next.delete(row.key) : next.add(row.key);
      return next;
    });
  };

  const toggleSelectRows = (rows, shouldSelect) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      rows.forEach((row) => {
        if (!row.selectable) return;
        shouldSelect ? next.add(row.key) : next.delete(row.key);
      });
      return next;
    });
  };

  const handleSaveAgentAdjustment = async (amount, reason) => {
    if (!run || !agentAdjustmentTarget) return;

    setIsSavingAgentAdjustment(true);
    try {
      if (agentAdjustmentTarget.mode === "edit") {
        const adjustmentId = getAdjustmentId(agentAdjustmentTarget.adjustment);
        if (!adjustmentId) {
          showToastPayload(payoutRunToasts.invalidAdjustmentUpdate());
          return;
        }

        await saveAgentAdjustment({
          payoutRunId: run.payoutRunId,
          mode: "edit",
          adjustmentId,
          amount,
          reason,
        });
      } else {
        await saveAgentAdjustment({
          payoutRunId: run.payoutRunId,
          mode: "add",
          agentId: agentAdjustmentTarget.agentId,
          agentName: agentAdjustmentTarget.agentLabel,
          amount,
          reason,
        });
      }
    } catch {
      showToastPayload(
        payoutRunToasts.adjustmentSaveError(agentAdjustmentTarget.mode),
      );
    } finally {
      setIsSavingAgentAdjustment(false);
      setAgentAdjustmentTarget(null);
    }
  };

  const handleDeleteAgentAdjustment = async (adjustment) => {
    if (isDeletingAdjustment) return;
    if (!run) return;
    const id = getAdjustmentId(adjustment);
    if (id == null) {
      showToastPayload(payoutRunToasts.invalidAdjustmentDelete());
      return;
    }

    setIsDeletingAdjustment(true);

    try {
      await deleteAgentAdjustment({
        payoutRunId: run.payoutRunId,
        adjustmentId: id,
      });
      showToastPayload(payoutRunToasts.adjustmentDeleted());
    } catch {
      showToastPayload(payoutRunToasts.adjustmentDeleteError());
    } finally {
      setIsDeletingAdjustment(false);
      setDeleteAdjustmentTarget(null);
    }
  };

  const handleApproveRecord = async (target) => {
    if (!run || !target) return;

    const recordKey = getRecordActionKey(target);
    if (!recordKey || approvingRecordKeys.has(recordKey)) return;

    addRecordKey(setApprovingRecordKeys, recordKey);
    try {
      await updateRecordStatus({
        target,
        status: "Approved",
        payoutRunId: run.payoutRunId,
      });
      await fetchRun(run.payoutRunId, { showLoader: false });
    } catch {
      showToastPayload(payoutRunToasts.approveError(target));
    } finally {
      removeRecordKey(setApprovingRecordKeys, recordKey);
    }
  };

  const handleRejectRecord = async (reason) => {
    if (!run || !rejectTarget || isSubmittingRecordAction) return;

    setIsSubmittingRecordAction(true);
    try {
      await updateRecordStatus({
        target: rejectTarget,
        status: "Rejected",
        payoutRunId: run.payoutRunId,
        notes: reason,
      });

      await fetchRun(run.payoutRunId, { showLoader: false });
    } catch {
      showToastPayload(payoutRunToasts.declineError(rejectTarget));
    } finally {
      setIsSubmittingRecordAction(false);
      setRejectTarget(null);
    }
  };

  const handleAdjustRecord = async (amount, reason) => {
    if (!run || !adjustTarget || isSubmittingRecordAction) return;

    setIsSubmittingRecordAction(true);
    try {
      await addRecordAdjustment({
        target: adjustTarget,
        amount,
        reason,
        payoutRunId: run.payoutRunId,
      });
      await fetchRun(run.payoutRunId, { showLoader: false });
    } catch {
      showToastPayload(payoutRunToasts.adjustError(adjustTarget));
    } finally {
      setIsSubmittingRecordAction(false);
      setAdjustTarget(null);
    }
  };

  const handleRemoveRecord = async (target) => {
    if (!run || !target) return;

    const recordKey = getRecordActionKey(target);
    if (!recordKey || removingRecordKeys.has(recordKey)) return;

    addRecordKey(setRemovingRecordKeys, recordKey);
    try {
      await sendRecordBackToPool({
        target,
        payoutRunId: run.payoutRunId,
        status: "Pending",
      });

      await fetchRun(run.payoutRunId, { showLoader: false });
      showToastPayload(payoutRunToasts.removeSuccess(target));
    } catch {
      showToastPayload(payoutRunToasts.removeError(target));
    } finally {
      removeRecordKey(setRemovingRecordKeys, recordKey);
    }
  };

  const handleReopenRecord = async (target) => {
    if (!run || !target) return;

    const recordKey = getRecordActionKey(target);
    if (!recordKey || approvingRecordKeys.has(recordKey)) return;

    addRecordKey(setApprovingRecordKeys, recordKey);
    try {
      await updateRecordStatus({
        target,
        status: "Pending",
        payoutRunId: run.payoutRunId,
      });
      await fetchRun(run.payoutRunId, { showLoader: false });
    } catch {
      showToastPayload(payoutRunToasts.reopenError(target));
    } finally {
      removeRecordKey(setApprovingRecordKeys, recordKey);
    }
  };

  const runBulkAction = async (targets, action, performAction) => {
    if (!run || !Array.isArray(targets) || targets.length === 0) return;
    if (isSubmittingRecordAction) return;

    setIsSubmittingRecordAction(true);
    try {
      let successCount = 0;

      for (const target of targets) {
        try {
          await performAction(target);
          successCount += 1;
        } catch {
          // Continue processing remaining selected rows even if one fails.
        }
      }

      await fetchRun(run.payoutRunId, { showLoader: false });

      if (successCount === targets.length) {
        showToast(
          `${successCount} record${successCount === 1 ? "" : "s"} ${action}.`,
          "success",
          action,
        );
      } else {
        showToast(
          `${successCount} of ${targets.length} record${targets.length === 1 ? "" : "s"} ${action}.`,
          "warning",
          action,
        );
      }
    } catch {
      showToast("Unable to update the selected records.", "danger", action);
    } finally {
      setIsSubmittingRecordAction(false);
    }
  };

  const handleBulkUpdateRecords = async (targets, status, action) =>
    runBulkAction(targets, action, (target) =>
      updateRecordStatus({ target, status, payoutRunId: run.payoutRunId }),
    );

  const handleBulkApproveSelection = async () => {
    await handleBulkUpdateRecords(
      selectedRows.map((r) => r.target),
      "Approved",
      "approved",
    );
    clearSelection();
  };

  const handleBulkDeclineSelection = async () => {
    setBulkRejectTarget({
      label: `${selectedRows.length} selected records`,
      targets: selectedRows.map((r) => r.target),
    });
  };

  const handleBulkRejectRecords = async (reason) => {
    if (!bulkRejectTarget) return;

    await runBulkAction(bulkRejectTarget.targets, "declined", (target) =>
      updateRecordStatus({
        target,
        status: "Rejected",
        payoutRunId: run.payoutRunId,
        notes: reason,
      }),
    );
    clearSelection();
  };

  const handleBulkReturnToPoolSelection = async () => {
    await runBulkAction(
      selectedRows.map((r) => r.target),
      "returned to the pool",
      (target) =>
        sendRecordBackToPool({
          target,
          payoutRunId: run.payoutRunId,
          status: "Pending",
        }),
    );
    clearSelection();
  };

  const handleApproveAgentGroup = async (agentGroup) => {
    const targets = buildSelectionRows([agentGroup])
      .filter((row) => row.selectable)
      .map((row) => row.target);

    if (targets.length === 0 || approvingAgentKey) return;

    setApprovingAgentKey(agentGroup.key);
    try {
      await handleBulkUpdateRecords(targets, "Approved", "approved");
    } finally {
      setApprovingAgentKey(null);
    }
  };

  const handleRunDecisionConfirm = async (decision) => {
    if (!run || !decision) return;

    setIsSubmittingRunDecision(true);
    try {
      await submitRunDecision({
        id: run.payoutRunId,
        action: decision.action,
        notes: decision.reason,
        removeContractsToPoolOnDecline: decision.removeContractsToPoolOnDecline,
      });
      await fetchRun(run.payoutRunId, { showLoader: false });
      showToastPayload(payoutRunToasts.runDecisionSuccess(decision.action));
    } catch {
      showToastPayload(payoutRunToasts.runDecisionError(decision.action));
    } finally {
      setIsSubmittingRunDecision(false);
      setRunDecision(null);
    }
  };

  const getAgentIdFromKey = (agentKey) => {
    if (!agentKey || agentKey === "all") return null;
    if (!agentKey.startsWith("agent:")) return null;

    const parsed = Number(agentKey.replace("agent:", ""));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const handleRequestCommissionReport = async ({
    reportId,
    fileType,
    agentKey,
  }) => {
    if (!run || isSubmittingReportRequest) return;

    const parsedReportId = Number(reportId);
    if (!Number.isFinite(parsedReportId)) {
      showToastPayload(payoutRunToasts.reportRequestError());
      setIsRequestReportModalOpen(false);
      return;
    }

    setIsSubmittingReportRequest(true);
    try {
      const response = await requestCommissionReport({
        payoutRunId: run.payoutRunId,
        reportId: parsedReportId,
        outputFormat: fileType,
        agentId: getAgentIdFromKey(agentKey),
      });
      const disposition = response?.headers?.["content-disposition"];
      const filename = parseFilenameFromDisposition(disposition);
      triggerDownload(response?.data, filename);
      showToastPayload(payoutRunToasts.reportRequestSuccess());
    } catch {
      showToastPayload(payoutRunToasts.reportRequestError());
    } finally {
      setIsSubmittingReportRequest(false);
      setIsRequestReportModalOpen(false);
    }
  };

  const handleDownloadCommissionReport = async (report) => {
    if (!run) return;

    const referenceId = report?.referenceId ?? report?.ReferenceId;
    if (!referenceId) {
      showToastPayload(payoutRunToasts.reportRequestError());
      return;
    }

    try {
      const response = await downloadCommissionReport({
        payoutRunId: run.payoutRunId,
        referenceId,
      });
      const disposition = response?.headers?.["content-disposition"];
      const filename =
        parseFilenameFromDisposition(disposition) ||
        report?.archiveFileName ||
        `${referenceId}.zip`;
      triggerDownload(response?.data, filename);
      showToastPayload(payoutRunToasts.reportRequestSuccess());
    } catch {
      showToastPayload(payoutRunToasts.reportRequestError());
    }
  };

  const handleAddContractToPayoutRun = async ({ contract }) => {
    if (!run || isSubmittingAddContract) return;

    const isChargeback = contract?.recordType === "chargeback";
    const agentContractIds = isChargeback
      ? []
      : (contract?.agentContractIds ??
        (contract?.commissionAgentContractId
          ? [contract.commissionAgentContractId]
          : []));
    const chargebackRecordIds = isChargeback
      ? (contract?.chargebackRecordIds ??
        (contract?.chargebackRecordId ? [contract.chargebackRecordId] : []))
      : [];
    if (
      isChargeback
        ? chargebackRecordIds.length === 0
        : agentContractIds.length === 0
    ) {
      showToastPayload(payoutRunToasts.addContractError());
      setIsAddContractModalOpen(false);
      return;
    }

    setIsSubmittingAddContract(true);
    try {
      if (isChargeback) {
        await addChargebackToPayoutRun({
          payoutRunId: run.payoutRunId,
          chargebackRecordIds,
        });
      } else {
        await addAgentContractToPayoutRun({
          payoutRunId: run.payoutRunId,
          agentContractIds,
        });
      }
      await fetchRun(run.payoutRunId, { showLoader: false });
      showToastPayload(payoutRunToasts.addContractSuccess());
    } catch {
      showToastPayload(payoutRunToasts.addContractError());
    } finally {
      setIsSubmittingAddContract(false);
      setIsAddContractModalOpen(false);
    }
  };

  const handleOpenRequestReportModal = async () => {
    setIsRequestReportModalOpen(true);
    try {
      await fetchCommissionReportTypes();
    } catch {
      showToastPayload(payoutRunToasts.reportTypeLoadError());
    }
  };

  const handleOpenAddContractModal = () => {
    setIsAddContractModalOpen(true);
  };

  return (
    <div className="commissions-scope min-vh-100 text-body">
      <ToastMessage
        message={toast.message}
        show={toast.show}
        handleToast={hideToast}
        type={toast.type}
        action={toast.action}
      />
      <Container
        className="py-2 py-sm-3"
        style={{
          paddingBottom: selectedRows.length > 0 ? "8rem" : undefined,
        }}
      >
        <Button
          as={Link}
          to="/commissions-payout-runs"
          variant="link"
          className="p-0 mb-2 text-decoration-none fw-semibold d-inline-flex align-items-center gap-1"
          style={{ fontSize: "0.8125rem" }}
        >
          <ArrowLeft size={14} /> Back to payout runs
        </Button>
        <PageHeader title="Payout run">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <HeaderButton
              text="Request report"
              variant="outline-primary"
              onClick={handleOpenRequestReportModal}
              disabled={!run || isDeclined}
              icon={<FileEarmarkBarGraph size={14} />}
            />
            <HeaderButton
              text="Add missing record"
              variant="outline-secondary"
              onClick={handleOpenAddContractModal}
              disabled={!run || isLocked}
              icon={<Plus size={14} />}
            />
          </div>
        </PageHeader>
        {isLoading ? (
          <LoadingSpinner />
        ) : showNotFoundAlert ? (
          <Alert variant="warning">Payout run not found.</Alert>
        ) : (
          <>
            <RunSummaryCard
              run={run}
              isLocked={isLocked}
              counts={counts}
              onViewReports={() => setIsReportsModalOpen(true)}
              onApprove={() => setRunDecision("approve")}
              onDecline={() => setRunDecision("decline")}
            />

            {groupedByAgent.length === 0 ? (
              <Alert variant="light" className="empty-alert">
                No payout records or adjustments found for this run.
              </Alert>
            ) : (
              <div className={isLocked ? "run-locked-scope" : undefined}>
                {groupedByAgent.map((agentGroup) => (
                  <AgentPanel
                    key={agentGroup.key}
                    agentGroup={agentGroup}
                    isCollapsed={collapsed.has(agentGroup.key)}
                    onToggleCollapse={() => toggleCollapse(agentGroup.key)}
                    expandedCommissionRows={expandedCommissionRows}
                    onToggleCommissionRow={toggleCommissionRow}
                    onAdjust={isLocked ? undefined : setAdjustTarget}
                    onReject={isLocked ? undefined : setRejectTarget}
                    onApprove={isLocked ? undefined : handleApproveRecord}
                    selectedKeys={selectedKeys}
                    onToggleSelect={toggleSelectRow}
                    onToggleSelectAll={toggleSelectRows}
                    isSelectionDisabled={isLocked || isSubmittingRecordAction}
                    areRecordActionsDisabled={isSubmittingRecordAction}
                    onApproveAgent={
                      isLocked ? undefined : handleApproveAgentGroup
                    }
                    isApprovingAgent={approvingAgentKey === agentGroup.key}
                    onRemove={isLocked ? undefined : handleRemoveRecord}
                    onReopen={isLocked ? undefined : handleReopenRecord}
                    isLocked={isLocked}
                    onAddAgentAdjustment={
                      isLocked
                        ? undefined
                        : (agentGroup) => {
                            const key = agentGroup.key || "";
                            if (!key.startsWith("agent:")) {
                              showToastPayload(
                                payoutRunToasts.invalidAgentForAdjustment(),
                              );
                              return;
                            }

                            setAgentAdjustmentTarget({
                              mode: "add",
                              agentId: Number(key.replace("agent:", "")),
                              agentLabel: agentGroup.agentLabel,
                            });
                          }
                    }
                    onEditAgentAdjustment={
                      isLocked
                        ? undefined
                        : (adjustment) =>
                            setAgentAdjustmentTarget({
                              mode: "edit",
                              agentId: adjustment.agentId,
                              agentLabel: agentGroup.agentLabel,
                              adjustment,
                            })
                    }
                    onDeleteAgentAdjustment={
                      isLocked
                        ? undefined
                        : (adjustment) => {
                            if (isDeletingAdjustment) return;
                            setDeleteAdjustmentTarget(adjustment);
                          }
                    }
                    deletingAdjustmentIds={
                      isDeletingAdjustment &&
                      getAdjustmentId(deleteAdjustmentTarget) != null
                        ? new Set([getAdjustmentId(deleteAdjustmentTarget)])
                        : undefined
                    }
                    approvingRecordKeys={approvingRecordKeys}
                    removingRecordKeys={removingRecordKeys}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </Container>

      {!isLocked && selectedRows.length > 0 && (
        <PayoutRunActionBar
          selectedCount={selectedRows.length}
          selectedAmount={selectionSummary.amount}
          contractCount={selectionSummary.contracts}
          bonusCount={selectionSummary.bonuses}
          chargebackCount={selectionSummary.chargebacks}
          isSaving={isSubmittingRecordAction}
          onRequestDecline={handleBulkDeclineSelection}
          onClear={clearSelection}
          onApprove={handleBulkApproveSelection}
          onReturnToPool={handleBulkReturnToPoolSelection}
        />
      )}

      {adjustTarget && (
        <AdjustModal
          target={adjustTarget}
          onCancel={() => setAdjustTarget(null)}
          onSave={handleAdjustRecord}
          isSubmitting={isSubmittingRecordAction}
        />
      )}
      {agentAdjustmentTarget && (
        <AgentAdjustmentModal
          agentLabel={agentAdjustmentTarget.agentLabel}
          initial={
            agentAdjustmentTarget.mode === "edit"
              ? agentAdjustmentTarget.adjustment
              : null
          }
          onCancel={() => setAgentAdjustmentTarget(null)}
          onSave={handleSaveAgentAdjustment}
          isSubmitting={isSavingAgentAdjustment}
        />
      )}
      {rejectTarget && (
        <RejectModal
          target={rejectTarget}
          onCancel={() => setRejectTarget(null)}
          onConfirm={handleRejectRecord}
          isSubmitting={isSubmittingRecordAction}
        />
      )}
      {bulkRejectTarget && (
        <RejectModal
          target={bulkRejectTarget}
          onCancel={() => setBulkRejectTarget(null)}
          onConfirm={handleBulkRejectRecords}
          isSubmitting={isSubmittingRecordAction}
        />
      )}
      {runDecision && run && (
        <RunDecisionModal
          action={runDecision}
          run={run}
          pendingCount={counts.pending}
          isSubmitting={isSubmittingRunDecision}
          onCancel={() => setRunDecision(null)}
          onConfirm={handleRunDecisionConfirm}
        />
      )}
      {deleteAdjustmentTarget && (
        <ConfirmActionModal
          show
          title="Delete agent adjustment"
          message="This adjustment will be removed from the payout run immediately."
          confirmLabel="Delete adjustment"
          confirmVariant="danger"
          isSubmitting={isDeletingAdjustment}
          onCancel={() => {
            if (isDeletingAdjustment) return;
            setDeleteAdjustmentTarget(null);
          }}
          onConfirm={() => handleDeleteAgentAdjustment(deleteAdjustmentTarget)}
        />
      )}
      {isRequestReportModalOpen && (
        <RequestReportModal
          agents={reportAgentOptions}
          reportTypes={reportTypeOptions}
          isLoadingReportTypes={isLoadingReportTypes}
          isSubmitting={isSubmittingReportRequest}
          onCancel={() => setIsRequestReportModalOpen(false)}
          onSubmit={handleRequestCommissionReport}
        />
      )}
      {isReportsModalOpen && (
        <PayoutRunReportsModal
          reports={run?.reports || []}
          totalCount={run?.reports?.length || 0}
          runName={run?.name}
          onDownloadReport={handleDownloadCommissionReport}
          onCancel={() => setIsReportsModalOpen(false)}
          onRequestReport={() => {
            setIsReportsModalOpen(false);
            handleOpenRequestReportModal();
          }}
        />
      )}
      {isAddContractModalOpen && (
        <AddContractModal
          api={props.api}
          payoutRunId={run?.payoutRunId}
          isSubmitting={isSubmittingAddContract}
          onCancel={() => setIsAddContractModalOpen(false)}
          onSubmit={handleAddContractToPayoutRun}
          onError={() =>
            showToast(
              "Unable to search for unassigned records.",
              "danger",
              "error",
            )
          }
        />
      )}
    </div>
  );
};

function RunSummaryCard({
  run,
  isLocked,
  counts,
  onViewReports,
  onApprove,
  onDecline,
}) {
  if (!run) return null;

  const reviewed = counts.approved + counts.rejected;
  const pct = counts.total ? Math.round((reviewed / counts.total) * 100) : 0;
  const isDeclined = run.status === "declined" || run.status === "rejected";
  const statusVerb = isDeclined ? "Declined" : "Approved";
  const lockedLabel = isDeclined
    ? "Declined"
    : run.status === "paid"
      ? "Paid"
      : "Approved";
  const summaryCardToneClass = isLocked
    ? isDeclined
      ? "run-summary-card--declined"
      : "run-summary-card--approved"
    : "";

  const reports = run.reports ?? [];
  const latestReport = run.latestReport ?? reports[0] ?? null;
  const displayedReportCount = reports.length;
  const reportMetaDetails = formatReportMetaDetails(latestReport);

  return (
    <Card className={`run-summary-card mb-4 ${summaryCardToneClass}`.trim()}>
      <Card.Body>
        <div className="run-summary-strip">
          <div className="run-summary-identity min-w-0">
            <div className="run-summary-status-row">
              {isLocked ? (
                <span
                  className={`run-status-chip ${
                    isDeclined
                      ? "run-status-chip--declined"
                      : "run-status-chip--locked"
                  }`}
                >
                  <Lock size={12} /> {lockedLabel}
                </span>
              ) : (
                <span className="run-status-chip run-status-chip--open">
                  <Unlock size={12} color="currentColor" /> Open
                </span>
              )}
              <span
                className={`run-summary-period ${isLocked ? "run-summary-period--locked" : "run-summary-period--open"}`}
              >
                <FormattedDate date={run.periodStart} /> –{" "}
                <FormattedDate date={run.periodEnd} />
              </span>
            </div>
            <h2 className="run-summary-name">{run.name}</h2>
          </div>

          <div className="run-summary-metric">
            <span className="run-summary-metric-label">
              {isDeclined
                ? "Net payable, not paid"
                : isLocked
                  ? "Net paid"
                  : "Net payable · live"}
            </span>
            <span className="run-summary-metric-value tnum">
              {formatCurrency(run.netPayable)}
            </span>
          </div>
        </div>

        {latestReport && (
          <div className="run-summary-report-row">
            <span className="run-summary-report-file-icon" aria-hidden="true">
              <FileEarmarkBarGraph size={15} />
            </span>
            <div className="run-summary-report-copy">
              <strong className="run-summary-report-name">
                {latestReport?.reportName ||
                  (isDeclined ? "Payout summary" : "Final payout summary")}
              </strong>
              {reportMetaDetails.map((detail, idx) => (
                <span key={idx} className="run-summary-report-meta-item">
                  <span
                    className="run-summary-report-bullet"
                    aria-hidden="true"
                  >
                    •
                  </span>
                  <span>{detail}</span>
                </span>
              ))}
            </div>
            <div className="run-summary-report-actions">
              <button
                type="button"
                className="run-summary-report-all"
                onClick={onViewReports}
                disabled={reports.length === 0}
              >
                All reports ({displayedReportCount})
              </button>
            </div>
          </div>
        )}

        {!isLocked && counts.total > 0 && (
          <div className="run-summary-progress">
            <div className="run-summary-progress-meta">
              <span className="run-summary-progress-head">
                <span className="run-summary-progress-label">
                  Review progress
                </span>
                <span className="run-summary-progress-count-main">
                  <strong>{reviewed}</strong> of {counts.total} reviewed
                </span>
              </span>
              {(counts.pending > 0 || counts.rejected > 0) && (
                <span className="run-summary-progress-counts">
                  {counts.pending > 0 && (
                    <span className="run-summary-progress-count run-summary-progress-count--pending">
                      {counts.pending} pending
                    </span>
                  )}
                  {counts.rejected > 0 && (
                    <span className="run-summary-progress-count run-summary-progress-count--rejected">
                      {counts.rejected} declined
                    </span>
                  )}
                </span>
              )}
            </div>
            <ProgressBar
              now={pct}
              variant="success"
              className="run-summary-progress-bar"
            />
          </div>
        )}

        <div className="run-summary-details">
          <div className="run-summary-totals-wrapper">
            <SummaryTotals
              commissions={run.commissions}
              chargebacks={run.chargebacks}
              bonuses={run.bonuses}
              adjustments={run.adjustments}
              rejected={run.rejected}
            />
            {isLocked && counts.total > 0 && (
              <div className="run-summary-reviewed-count">
                {reviewed} of {counts.total} records reviewed
              </div>
            )}
            {!isLocked && (
              <div className="run-summary-actions-inline">
                <button
                  type="button"
                  className="run-summary-decline-btn"
                  onClick={onDecline}
                >
                  <XCircle size={14} />
                  <span>Decline run</span>
                </button>
                <button
                  type="button"
                  className="run-summary-approve-btn"
                  onClick={onApprove}
                >
                  <CheckCircle size={14} />
                  <span>Approve run</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {isLocked && (
          <div className="run-summary-meta run-summary-meta--locked">
            <span className="run-summary-meta-status" aria-hidden="true">
              {isDeclined ? <XCircle size={14} /> : <CheckCircle size={14} />}
            </span>
            <div className="run-summary-meta-signed">
              <span className="run-summary-meta-label">{statusVerb} by</span>
              <span className="run-summary-meta-value">
                {run.reviewedBy || "—"}
                {run.reviewedBy && run.lastUpdated && (
                  <>
                    {" "}
                    ·{" "}
                    <FormattedDate
                      date={run.lastUpdated}
                      format="MM/DD h:mm A"
                    />
                  </>
                )}
              </span>
            </div>
            <div className="run-summary-meta-notes">
              <span className="run-summary-meta-label">Notes</span>
              <span className="run-summary-meta-value">{run.notes || "—"}</span>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default withHeaderMessages(WithApiAccess(PayoutRun));
